import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, ButtonGroup, Button, Grid, Heading, Spinner, Text, useDisclosure } from '@chakra-ui/react'
import { keepPreviousData, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { PartyList } from './components/PartyList'
import { RequestList } from './components/RequestList'
import { CommunicationPane } from './components/CommunicationPane'
import { AttachToRequestModal } from './components/AttachToRequestModal'
import { CreateRequestFromEmailModal } from './components/CreateRequestFromEmailModal'
import {
  addInternalNote,
  attachEmail,
  createRequestFromEmail,
  getCaseParties,
  getParentStatusLabel,
  getPartyRequests,
  getQueue,
  getRequestMessages,
  ignoreEmail,
  markRequestWaiting,
  replyInThread,
} from '@/lib/services/caseThreadsService'
import { markEmailAsRead } from '@/lib/services/inboxService'
import { caseThreadDisplayStatus } from '@/lib/utils/caseThreads'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useInboxNotificationsStore } from '@/store/inboxNotifications'

type GroupBy = 'client' | 'requestor'
type QueueKind = 'unlinked' | 'needs_review'

const CASE_THREAD_POLL_MS = 15_000

export default function CaseThreadsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const toast = useToastNotification()
  const [groupBy, setGroupBy] = useState<GroupBy>('client')
  const [partyId, setPartyId] = useState<string | null>(null)
  const [queue, setQueue] = useState<QueueKind | null>(null)
  const [requestId, setRequestId] = useState<number | null>(null)
  const [emailId, setEmailId] = useState<number | null>(null)
  const attachModal = useDisclosure()
  const createModal = useDisclosure()
  const inboxTopId = useInboxNotificationsStore((state) => state.topId)
  const seenInboxTopId = useRef<number | null | undefined>(undefined)

  const parties = useQuery({
    queryKey: ['case-threads', 'parties', groupBy],
    queryFn: () => getCaseParties(groupBy),
    staleTime: 0,
    placeholderData: keepPreviousData,
    refetchInterval: (query) => (query.state.status === 'error' ? false : CASE_THREAD_POLL_MS),
  })
  const requests = useQuery({
    queryKey: ['case-threads', 'requests', groupBy, partyId],
    queryFn: () => getPartyRequests(groupBy, partyId as string),
    enabled: !!partyId && !queue,
    staleTime: 0,
    placeholderData: keepPreviousData,
    refetchInterval: CASE_THREAD_POLL_MS,
  })
  const queueRows = useQuery({
    queryKey: ['case-threads', 'queue', queue],
    queryFn: () => getQueue(queue === 'needs_review' ? 'needs-review' : 'unlinked'),
    enabled: !!queue,
    staleTime: 0,
    placeholderData: keepPreviousData,
    refetchInterval: CASE_THREAD_POLL_MS,
  })
  const thread = useQuery({
    queryKey: ['case-threads', 'messages', requestId],
    queryFn: () => getRequestMessages(requestId as number),
    enabled: !!requestId && !queue,
    staleTime: 0,
    placeholderData: keepPreviousData,
    refetchInterval: CASE_THREAD_POLL_MS,
  })

  const parentTicketIds = useMemo(() => {
    const ids = new Set<number>()
    for (const row of requests.data ?? []) {
      if (row.parent_ticket_id) ids.add(row.parent_ticket_id)
    }
    if (thread.data?.parent_ticket_id) ids.add(thread.data.parent_ticket_id)
    return [...ids]
  }, [requests.data, thread.data?.parent_ticket_id])
  const parentStatusQueries = useQueries({
    queries: parentTicketIds.map((id) => ({
      queryKey: ['case-threads', 'parent-status', id],
      queryFn: () => getParentStatusLabel(id),
      staleTime: 0,
      refetchInterval: CASE_THREAD_POLL_MS,
    })),
  })
  const parentLabels = useMemo(() => {
    const labels: Record<number, string> = {}
    parentTicketIds.forEach((id, index) => {
      const label = parentStatusQueries[index]?.data
      if (label) labels[id] = label
    })
    return labels
  }, [parentStatusQueries, parentTicketIds])

  const selectedEmail = queueRows.data?.find((row) => row.id === emailId) ?? null
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['case-threads'] })
  }

  useEffect(() => {
    if (seenInboxTopId.current === undefined) {
      seenInboxTopId.current = inboxTopId
      return
    }
    if (inboxTopId != null && inboxTopId !== seenInboxTopId.current) {
      seenInboxTopId.current = inboxTopId
      void queryClient.invalidateQueries({ queryKey: ['case-threads'] })
    }
  }, [inboxTopId, queryClient])

  useEffect(() => {
    if (!thread.dataUpdatedAt) return
    void queryClient.invalidateQueries({ queryKey: ['case-threads', 'parties'] })
    void queryClient.invalidateQueries({ queryKey: ['case-threads', 'requests'] })
  }, [thread.dataUpdatedAt, queryClient])

  useEffect(() => {
    if (!emailId) return
    void markEmailAsRead(emailId).catch(() => undefined)
  }, [emailId])

  const run = async (work: () => Promise<void>, success: string) => {
    try {
      await work()
      toast.showSuccess(success)
      refresh()
    } catch (error) {
      toast.showError(error)
    }
  }

  return (
    <Box h="calc(100vh - 120px)" display="flex" flexDirection="column">
      <Box px={4} py={3}>
        <Heading size="md">Case Threads</Heading>
        <Text fontSize="sm" color="gray.500">
          Client or requestor, then the ticket request, then the full email conversation. Mail does not create a parent ticket or task.
        </Text>
        <ButtonGroup mt={2} size="sm" isAttached>
          <Button variant={groupBy === 'client' ? 'solid' : 'outline'} onClick={() => { setGroupBy('client'); setPartyId(null); setQueue(null); setRequestId(null); setEmailId(null) }}>
            Clients
          </Button>
          <Button variant={groupBy === 'requestor' ? 'solid' : 'outline'} onClick={() => { setGroupBy('requestor'); setPartyId(null); setQueue(null); setRequestId(null); setEmailId(null) }}>
            Requestors
          </Button>
        </ButtonGroup>
      </Box>
      {parties.isLoading && !parties.data ? (
        <Spinner m={4} />
      ) : (
        <Grid
          flex="1"
          minH={0}
          templateColumns={{ base: '1fr', lg: '260px 320px 1fr' }}
          templateRows={{ base: 'auto auto 1fr', lg: '1fr' }}
        >
          <Box borderRightWidth={{ lg: '1px' }} borderBottomWidth={{ base: '1px', lg: 0 }} overflow="auto">
            {parties.isError ? (
              <Box px={3} py={2}>
                <Text fontSize="sm" color="gray.600">
                  Could not load case threads.
                </Text>
                <Button
                  mt={2}
                  size="sm"
                  variant="outline"
                  isLoading={parties.isFetching}
                  onClick={() => void parties.refetch()}
                >
                  Retry
                </Button>
              </Box>
            ) : null}
            <PartyList
              parties={parties.data?.data ?? []}
              selectedId={partyId}
              unlinkedCount={parties.data?.meta.unlinked_count ?? 0}
              needsReviewCount={parties.data?.meta.needs_review_count ?? 0}
              activeQueue={queue}
              onSelectParty={(id) => {
                setPartyId(id)
                setQueue(null)
                setRequestId(null)
                setEmailId(null)
              }}
              onSelectQueue={(next) => {
                setQueue(next)
                setPartyId(null)
                setRequestId(null)
                setEmailId(null)
              }}
            />
          </Box>
          <Box borderRightWidth={{ lg: '1px' }} overflow="auto">
            <RequestList
              requests={requests.data ?? []}
              parentLabels={parentLabels}
              queue={queue ? queueRows.data ?? [] : null}
              selectedRequestId={requestId}
              selectedEmailId={emailId}
              onSelectRequest={(id) => {
                setRequestId(id)
                setEmailId(null)
              }}
              onSelectEmail={(id) => {
                setEmailId(id)
                setRequestId(null)
              }}
            />
          </Box>
          <Box minH="320px" overflow="hidden">
            <CommunicationPane
              loading={thread.isLoading || queueRows.isLoading}
              queueEmail={selectedEmail}
              messages={thread.data?.messages ?? []}
              lockedSubject={thread.data?.locked_subject ?? ''}
              parentTicketId={thread.data?.parent_ticket_id ?? null}
              requestNumber={thread.data?.request.request_number}
              subject={thread.data?.request.subject}
              displayStatus={thread.data ? caseThreadDisplayStatus(thread.data.request, parentLabels) : null}
              canReply={(thread.data?.messages ?? []).some((message) => message.direction === 'inbound')}
              allowReplyAll={(thread.data?.participants.length ?? 0) > 0}
              onReply={(body, cc, replyAll, attachments) =>
                run(async () => {
                  if (requestId) await replyInThread(requestId, body, cc, replyAll, attachments)
                }, 'Reply saved on the case')
              }
              onNote={(body) =>
                run(async () => {
                  if (requestId) await addInternalNote(requestId, body)
                }, 'Internal note added')
              }
              onWaiting={() =>
                run(async () => {
                  if (requestId) await markRequestWaiting(requestId)
                }, 'Marked waiting on customer')
              }
              onOpenParent={() => {
                if (thread.data?.parent_ticket_id) navigate(`/tickets/${thread.data.parent_ticket_id}/view`)
              }}
              onAttach={attachModal.onOpen}
              onCreate={createModal.onOpen}
              onIgnore={() =>
                run(async () => {
                  if (emailId) await ignoreEmail(emailId)
                }, 'Email ignored')
              }
            />
          </Box>
        </Grid>
      )}
      <AttachToRequestModal
        isOpen={attachModal.isOpen}
        suggestedId={selectedEmail?.suggested_ticket_request_id}
        onClose={attachModal.onClose}
        onAttach={async (ticketRequestId) => {
          if (!emailId) return
          try {
            await attachEmail(emailId, ticketRequestId)
            toast.showSuccess('Email attached')
            refresh()
          } catch (error) {
            toast.showError(error)
            throw error
          }
        }}
      />
      <CreateRequestFromEmailModal
        isOpen={createModal.isOpen}
        onClose={createModal.onClose}
        onCreate={async (clientId) => {
          if (!emailId) return
          try {
            await createRequestFromEmail(emailId, clientId)
            toast.showSuccess('Ticket Request created')
            refresh()
          } catch (error) {
            toast.showError(error)
            throw error
          }
        }}
      />
    </Box>
  )
}
